import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Menu, X, MapPin, Calendar, Ticket } from "lucide-react";

export const Route = createFileRoute("/demo/voltage-lugano")({
  head: () => ({
    meta: [
      { title: "VOLTAGE Lugano — Underground Techno Event | Demo FlashWeb" },
      { name: "description", content: "VOLTAGE: l'evento techno underground di Lugano. Una notte. Tre stage. Suono in formato cinematico." },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Space+Mono:ital,wght@0,400;0,700;1,400&family=Anton+SC&display=swap" },
    ],
  }),
  component: VoltagePage,
});

const V1 = "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260622_083515_290e5a10-0b95-41af-a5e2-32b6389baa4d.mp4";
const V2 = "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260622_092455_089c54f8-3b03-4966-9df1-e9746063d0ef.mp4";
const V3 = "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260622_095810_ecea3dd2-fc5e-4e41-8696-4219290b6589.mp4";
const V4 = "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260622_095750_32a52ce0-2005-45c9-9093-41f03fde9530.mp4";
const V5 = "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260622_080203_fd7f4f85-3a86-4837-8192-85e7bfe68e75.mp4";

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*";

function ScrambleIn({ text, delay = 0, triggered }: { text: string; delay?: number; triggered: boolean }) {
  const [out, setOut] = useState("\u00A0");
  useEffect(() => {
    if (!triggered) return;
    let raf = 0;
    const t = setTimeout(() => {
      let frame = 0;
      const tick = () => {
        const reveal = frame * 0.5;
        let s = "";
        for (let i = 0; i < text.length; i++) {
          const c = text[i];
          if (c === " ") s += " ";
          else if (i < reveal) s += c;
          else if (i < reveal + 3) s += CHARS[Math.floor(Math.random() * CHARS.length)];
          else s += "";
        }
        setOut(s || "\u00A0");
        frame++;
        if (reveal < text.length + 3) raf = window.setTimeout(tick, 25) as unknown as number;
      };
      tick();
    }, delay);
    return () => { clearTimeout(t); clearTimeout(raf); };
  }, [text, delay, triggered]);
  return <span>{out}</span>;
}

function VoltageLogo({ size = 18, className = "" }: { size?: number; className?: string }) {
  return (
    <svg viewBox="-50 -50 100 100" width={size} height={size} className={className}>
      {[0, 90, 180, 270].map((r) => (
        <path
          key={r}
          transform={`rotate(${r})`}
          d="M 1.5,23 L 1.5,33 C 1.5,38.5 6,43 11.5,43 L 16.5,43 C 22,43 26.5,38.5 26.5,33 Q 28,28 33,26.5 C 38.5,26.5 43,22 43,16.5 L 43,11.5 C 43,6 38.5,1.5 33,1.5 L 23,1.5 Q 12,12 1.5,23 Z"
          fill="currentColor"
        />
      ))}
    </svg>
  );
}

function VoltagePage() {
  const [entered, setEntered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const section2Ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 600);
    return () => clearTimeout(t);
  }, []);

  const { scrollYProgress } = useScroll({ target: section2Ref, offset: ["start end", "end start"] });
  const smooth = useSpring(scrollYProgress, { stiffness: 15, damping: 32, mass: 1.8 });
  const ty = useTransform(smooth, [0, 1], [60, -120]);
  const op = useTransform(smooth, [0.2, 0.5], [0, 1]);

  return (
    <div style={{ fontFamily: "'Space Mono', monospace", background: "#000", color: "#fff" }} className="min-h-screen overflow-x-hidden antialiased">
      {/* NAVBAR */}
      <motion.nav
        initial={{ opacity: 0 }}
        animate={{ opacity: entered ? 1 : 0 }}
        transition={{ duration: 0.8 }}
        className="fixed inset-x-0 top-0 z-50 flex h-20 items-center justify-between px-4 sm:px-6 md:px-8"
      >
        <div className="flex items-center gap-2">
          <div className="flex h-12 items-center gap-2 rounded-[14px] bg-white/15 px-5 backdrop-blur-md">
            <VoltageLogo className="text-white" />
            <span className="text-[16px] font-medium tracking-tight">VOLTAGE</span>
          </div>
          <div className="hidden h-12 items-center gap-5 rounded-[14px] bg-white/15 px-5 backdrop-blur-md md:flex">
            <a href="#lineup" className="text-[14px] text-white/85 hover:text-white">Line-up</a>
            <a href="#info" className="text-[14px] text-white/85 hover:text-white">Info</a>
            <a href="#location" className="text-[14px] text-white/85 hover:text-white">Location</a>
          </div>
        </div>
        <a
          href="#tickets"
          className="hidden h-12 items-center gap-2 rounded-full bg-white px-6 text-[14px] font-medium text-black transition-transform hover:scale-[1.03] md:inline-flex"
        >
          <Ticket size={16} /> Tickets
        </a>
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-white/15 backdrop-blur-md md:hidden"
          aria-label="menu"
        >
          {menuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </motion.nav>

      {menuOpen && (
        <div className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-6 bg-black/95 backdrop-blur-xl md:hidden">
          {["Line-up", "Info", "Location", "Tickets"].map((l) => (
            <a key={l} href={`#${l.toLowerCase()}`} onClick={() => setMenuOpen(false)} className="text-3xl font-light text-white/90">{l}</a>
          ))}
        </div>
      )}

      {/* SECTION 1 — HERO */}
      <section className="relative h-screen min-h-[100dvh] w-full overflow-hidden">
        <video autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover">
          <source src={V1} type="video/mp4" />
        </video>
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "24px 24px" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70" />

        {/* watermark */}
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 select-none whitespace-nowrap text-center uppercase"
          style={{
            fontFamily: "'Anton SC', sans-serif",
            fontSize: "clamp(120px, 30vw, 521px)",
            letterSpacing: "-4px",
            lineHeight: 1,
            opacity: 0.1,
            transform: "translate(-50%, calc(-50% + 50px))",
            background: "radial-gradient(circle, rgba(220,38,38,0) 0%, #ef4444 70%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          VOLTAGE
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: entered ? 1 : 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 flex h-full flex-col px-4 pb-8 pt-24 sm:px-6 sm:pb-12 md:px-8"
        >
          <div className="flex-1" />
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="flex flex-col gap-4">
              <h1
                className="font-light leading-[0.95] tracking-[-0.03em] text-white"
                style={{ fontSize: "clamp(40px, 10vw, 100px)" }}
              >
                <span className="block"><ScrambleIn text="Una notte." delay={200} triggered={entered} /></span>
                <span className="block"><ScrambleIn text="Tre stage." delay={500} triggered={entered} /></span>
              </h1>
              <motion.p
                initial={{ y: 25, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.9, ease: [0.215, 0.61, 0.355, 1], delay: 1.2 }}
                className="max-w-sm text-[13px] leading-relaxed text-white/60 sm:text-[15px]"
              >
                Sabato 21 marzo 2026. Capannone industriale ex-Cornèr, Lugano.
                Dodici ore di techno in formato cinematico: line-up internazionale,
                impianto Funktion-One, visuals immersivi 360°.
              </motion.p>
            </div>
            <h1
              className="text-left font-light leading-[0.95] tracking-[-0.03em] text-white md:text-right"
              style={{ fontSize: "clamp(40px, 10vw, 100px)" }}
            >
              <span className="block"><ScrambleIn text="VOLTAGE" delay={700} triggered={entered} /></span>
              <span className="block"><ScrambleIn text="Lugano" delay={1000} triggered={entered} /></span>
            </h1>
          </div>
        </motion.div>
      </section>

      {/* SECTION 2 — CINEMATIC TEXT */}
      <section ref={section2Ref} className="relative h-screen min-h-[100dvh] w-full overflow-hidden">
        <video autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover">
          <source src={V2} type="video/mp4" />
        </video>
        <div className="absolute inset-x-0 top-0 z-10 h-[180px] bg-gradient-to-b from-[#010103] to-transparent" />
        <div className="relative z-20 flex h-full items-center justify-center" style={{ perspective: "400px" }}>
          <motion.p
            style={{ rotateX: 24, y: ty, translateZ: 15, opacity: op }}
            className="mx-auto max-w-5xl select-none px-6 text-center text-[22px] font-normal leading-[1.35] tracking-[-0.02em] text-white sm:px-12 sm:text-[30px] md:text-[36px] lg:text-[42px]"
          >
            Non è un concerto. Non è un club. È un'esperienza acustica progettata
            come un'opera. Ogni kick è una pulsazione, ogni hi-hat un riflesso.
            VOLTAGE trasforma la techno underground in un evento cinematico,
            costruito per chi cerca il suono nella sua forma più pura.
          </motion.p>
        </div>
      </section>

      {/* SECTION 3 — METRICS / LINEUP STATS */}
      <section id="lineup" className="relative min-h-screen w-full overflow-hidden">
        <video autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover">
          <source src={V3} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative mx-auto max-w-6xl px-6 pb-32 pt-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.2 }}
            className="mb-20 text-center text-[13px] uppercase tracking-[0.2em] text-white/40 sm:text-[14px]"
          >
            Line-up · 21.03.2026
          </motion.div>
          <div className="grid gap-16 md:grid-cols-3 md:gap-8">
            {[
              ["12h", "Di musica continua"],
              ["3", "Stage immersivi"],
              ["18", "Artist internazionali"],
            ].map(([n, l], i) => (
              <motion.div
                key={l}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, delay: i * 0.15 }}
                className="text-center md:text-left"
              >
                <div className="font-light leading-none tracking-[-0.04em] text-white" style={{ fontSize: "clamp(48px,10vw,96px)" }}>{n}</div>
                <div className="mt-4 text-[13px] tracking-wide text-white/40 sm:text-[15px]">{l}</div>
              </motion.div>
            ))}
          </div>

          <div className="mt-24 grid gap-x-12 gap-y-3 border-t border-white/10 pt-10 sm:grid-cols-2 md:grid-cols-3">
            {["AMELIE LENS", "CHARLOTTE DE WITTE", "I HATE MODELS", "SARA LANDRY", "TRYM", "KOBOSIL", "DAX J", "FJAAK", "999999999", "REINIER ZONNEVELD", "VTSS", "NUR JABER", "ANETHA", "SPFDJ", "+ MORE TBA"].map((n) => (
              <div key={n} className="border-b border-white/5 py-2 text-[14px] text-white/80">{n}</div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — INFO */}
      <section id="info" className="relative h-screen min-h-[100dvh] w-full overflow-hidden">
        <video autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover">
          <source src={V4} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative flex h-full flex-col px-8 py-12 sm:px-12 sm:py-16 md:px-16">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1 }}
              className="font-light leading-[0.95] tracking-[-0.03em] text-white"
              style={{ fontSize: "clamp(36px,8vw,72px)" }}
            >
              Costruito <br /> per il suono.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="max-w-xs text-[13px] leading-relaxed text-white/50 sm:text-[15px] md:pt-2 md:text-right"
            >
              Capienza limitata a 2.400 ingressi. Impianto Funktion-One Resolution 5,
              acustica calibrata da Audio Brewers Zurich. Nessun compromesso.
            </motion.p>
          </div>
          <div className="flex-1" />
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="grid grid-cols-2 gap-8 md:grid-cols-4 md:gap-6"
          >
            {[
              ["Sound", "Funktion-One Resolution 5 su tutti e tre gli stage."],
              ["Visuals", "Mapping immersivo 360° firmato studio Resolume Berlin."],
              ["Capienza", "Limitata a 2.400 ingressi per esperienza acustica ottimale."],
              ["Bar", "Cocktail bar dedicato + acqua gratuita illimitata."],
            ].map(([t, d], i) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
              >
                <div className="mb-2 text-[14px] text-white sm:text-[16px]">{t}</div>
                <div className="text-[12px] leading-relaxed text-white/40 sm:text-[14px]">{d}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 5 — TICKETS */}
      <section id="tickets" className="min-h-screen bg-black">
        <div className="mx-auto max-w-3xl px-6 py-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1 }}
          >
            <div className="mb-8 text-[13px] uppercase tracking-[0.2em] text-white/40 sm:text-[14px]">Tickets</div>
            <h2 className="mb-10 font-light leading-[1.15] tracking-[-0.02em] text-white" style={{ fontSize: "clamp(28px,6vw,56px)" }}>
              Tre fasi. Zero ripensamenti.
            </h2>
            <p className="mx-auto max-w-xl text-[15px] leading-relaxed text-white/45 sm:text-[17px]">
              Le prevendite seguono tre release temporizzati. Ogni release è limitato:
              quando finisce, finisce. Nessun ticket alla porta.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1.2, delay: 0.4 }}
            className="mt-20 flex flex-col items-center gap-4"
          >
            {[
              ["Phase 1", "CHF 45", "Sold out"],
              ["Phase 2", "CHF 65", "In vendita"],
              ["Phase 3", "CHF 85", "Disponibile dal 01.03"],
            ].map(([phase, price, status]) => (
              <div key={phase} className="flex h-[72px] w-full max-w-md items-center justify-between rounded-lg border border-white/10 px-6">
                <div className="text-[12px] uppercase tracking-[0.15em] text-white/30">{phase}</div>
                <div className="flex items-center gap-4">
                  <span className="text-[16px] font-light text-white sm:text-[18px]">{price}</span>
                  <span className="text-[11px] text-white/40">{status}</span>
                </div>
              </div>
            ))}
            <a
              href="https://wa.me/41782369200?text=Voglio%20il%20mio%20ticket%20VOLTAGE%20Lugano"
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3 text-[14px] font-medium text-black transition-transform hover:scale-[1.03]"
            >
              <Ticket size={16} /> Acquista ora
            </a>
          </motion.div>
        </div>
      </section>

      {/* LINEUP / PRESS */}
      <section className="border-t border-white/5 bg-black px-6 py-24 sm:px-10">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 flex items-end justify-between gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-red-400/80">Press · radio · media</div>
              <h2 className="mt-3 text-3xl font-light tracking-tight text-white sm:text-5xl">Ne hanno parlato.</h2>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-5 text-white/45">
            <span className="text-lg sm:text-xl">Resident Advisor</span>
            <span className="text-white/15">/</span>
            <span className="text-lg italic sm:text-xl">Mixmag Italia</span>
            <span className="text-white/15">/</span>
            <span className="text-lg sm:text-xl">RSI Rete Tre</span>
            <span className="text-white/15">/</span>
            <span className="text-lg italic sm:text-xl">DJ Mag</span>
            <span className="text-white/15">/</span>
            <span className="text-lg sm:text-xl">Radio 3i</span>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-white/5 bg-black px-6 py-24 sm:px-10">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12">
            <div className="text-[11px] uppercase tracking-[0.3em] text-red-400/80">Voci dal floor</div>
            <h2 className="mt-3 text-3xl font-light tracking-tight text-white sm:text-5xl">L'edizione 2025.</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { q: "Lineup serio, impianto Funktion-One e una location pazzesca. La techno migliore arrivata in Ticino negli ultimi anni.", n: "Giulia M.", r: "Edizione 2025 · Milano" },
              { q: "Organizzazione impeccabile: ingresso veloce, guardaroba efficiente, prezzi onesti al bar. Il main stage tirato fino alle 9 del mattino.", n: "Davide R.", r: "Edizione 2025 · Bellinzona" },
              { q: "Bellissima curatela artistica. Tre stage davvero diversi e una scenografia industriale che esalta il suono. Ci torno sicuro.", n: "Sofia K.", r: "Edizione 2025 · Como" },
            ].map((t, i) => (
              <figure key={i} className="flex h-full flex-col justify-between rounded-lg border border-white/10 bg-white/[0.02] p-7">
                <blockquote className="text-[15px] leading-relaxed text-white/85">"{t.q}"</blockquote>
                <figcaption className="mt-6 border-t border-white/10 pt-5 text-[12px] uppercase tracking-[0.2em]">
                  <div className="text-red-400">{t.n}</div>
                  <div className="mt-1 text-white/40">{t.r}</div>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-2 gap-6 border-t border-white/10 pt-12 sm:grid-cols-4">
            {[
              ["3.200", "Ravers · ed. 2025"],
              ["12h", "Non-stop"],
              ["18", "Artists · 2026"],
              ["100%", "Sold out 2025"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="text-4xl font-light text-white sm:text-5xl">{n}</div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-white/45">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* FOOTER */}
      <footer id="location" className="flex min-h-[400px] flex-col overflow-hidden bg-black md:flex-row">

        <div className="relative h-[300px] md:h-auto md:w-1/2">
          <video autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover">
            <source src={V5} type="video/mp4" />
          </video>
        </div>
        <div className="flex flex-col justify-between p-10 sm:p-16 md:w-1/2">
          <div>
            <div className="mb-8 flex items-center gap-2 text-white/70">
              <VoltageLogo />
              <span className="text-[15px] font-medium tracking-tight">VOLTAGE</span>
            </div>
            <p className="max-w-sm text-[14px] leading-relaxed text-white/40 sm:text-[15px]">
              L'evento techno underground di Lugano. Costruito per chi cerca
              il suono nella sua forma più pura — senza compromessi.
            </p>
            <div className="mt-10 space-y-3 text-[13px] text-white/60">
              <div className="flex items-center gap-3"><Calendar size={14} className="text-red-400" /> Sabato 21 marzo 2026 · 22:00 → 10:00</div>
              <div className="flex items-center gap-3"><MapPin size={14} className="text-red-400" /> Ex-Cornèr Warehouse · Via Cantonale, Lugano</div>
            </div>
          </div>
          <div className="mt-12 text-[12px] text-white/25">© 2026 VOLTAGE Lugano · Demo by FlashWeb Agency</div>
        </div>
      </footer>
    </div>
  );
}
